"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MEDIA_NODE_PROFILE_BY_SLUG = exports.RUNNER_PROFILE_CAPABILITIES = exports.RUNNER_PROFILES = exports.RunnerProfileResolutionError = void 0;
exports.joinRunnerProfiles = joinRunnerProfiles;
exports.runnerProfileSatisfies = runnerProfileSatisfies;
exports.parseRunnerProfile = parseRunnerProfile;
exports.parseRunnerCapabilities = parseRunnerCapabilities;
exports.resolveRunnerRequirements = resolveRunnerRequirements;
exports.resolveRunnerProfileForStep = resolveRunnerProfileForStep;
const step_type_1 = require("./step-type");
class RunnerProfileResolutionError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RunnerProfileResolutionError';
    }
}
exports.RunnerProfileResolutionError = RunnerProfileResolutionError;
exports.RUNNER_PROFILES = [
    'core',
    'python',
    'media',
    'media-heavy',
];
exports.RUNNER_PROFILE_CAPABILITIES = {
    core: ['ai', 'control', 'http', 'javascript', 'service', 'transform'],
    python: [
        'ai',
        'control',
        'http',
        'javascript',
        'python',
        'service',
        'transform',
    ],
    media: [
        'ai',
        'control',
        'ffmpeg',
        'http',
        'javascript',
        'media',
        'python',
        'service',
        'transform',
    ],
    'media-heavy': [
        'ai',
        'control',
        'ffmpeg',
        'http',
        'image',
        'javascript',
        'media',
        'pdf',
        'python',
        'service',
        'transform',
    ],
};
const RUNNER_PROFILE_RANK = {
    core: 0,
    python: 1,
    media: 2,
    'media-heavy': 3,
};
const CORE_STEP_TYPES = new Set([
    'ai',
    'http',
    'service',
    'branch_or',
    'branch_xor',
    'branch_and',
    'merge',
    'loop',
    'split',
    'filter',
    'join',
    'output_sanitizer',
    'json_extract',
    'start',
    'end',
]);
exports.MEDIA_NODE_PROFILE_BY_SLUG = {
    'media.video_transcode': 'media-heavy',
    'media.image_resize': 'media-heavy',
    'media.document_to_pdf': 'media-heavy',
    'media.pdf_extract_text': 'media-heavy',
    'media.extract_audio': 'media',
    'media.audio_compress': 'media',
    'media.audio_cut': 'media',
};
function joinRunnerProfiles(left, right) {
    return RUNNER_PROFILE_RANK[left] >= RUNNER_PROFILE_RANK[right] ? left : right;
}
function runnerProfileSatisfies(actual, required) {
    return RUNNER_PROFILE_RANK[actual] >= RUNNER_PROFILE_RANK[required];
}
function parseRunnerProfile(value) {
    if (exports.RUNNER_PROFILES.includes(value)) {
        return value;
    }
    throw new RunnerProfileResolutionError(`Unknown runner profile "${value}"`);
}
function parseRunnerCapabilities(value) {
    if (!value?.trim())
        return [];
    return value
        .split(',')
        .map(part => part.trim())
        .filter(Boolean);
}
function resolveRunnerRequirements(definition) {
    const rawSteps = definition.steps;
    if (!Array.isArray(rawSteps)) {
        throw new RunnerProfileResolutionError('Workflow definition missing steps array');
    }
    let profile = 'core';
    for (const rawStep of rawSteps) {
        if (!rawStep || typeof rawStep !== 'object') {
            throw new RunnerProfileResolutionError('Workflow definition contains an invalid step');
        }
        profile = joinRunnerProfiles(profile, resolveRunnerProfileForStep(rawStep));
    }
    return {
        profile,
        capabilities: exports.RUNNER_PROFILE_CAPABILITIES[profile],
    };
}
function resolveRunnerProfileForStep(step) {
    const stepType = asString(step.type);
    if (!stepType || !(0, step_type_1.isValidStepType)(stepType)) {
        throw new RunnerProfileResolutionError(`Workflow step "${formatStepId(step)}" has unknown type "${String(step.type)}"`);
    }
    if (stepType === 'code') {
        const runtime = readNestedString(step, 'runtime') ??
            readNestedString(step.config, 'runtime') ??
            readNestedString(step.parameters, 'runtime');
        if (!runtime || runtime === 'javascript' || runtime === 'js') {
            return 'core';
        }
        if (runtime === 'python')
            return 'python';
        throw new RunnerProfileResolutionError(`Workflow code step "${formatStepId(step)}" has unsupported runtime "${runtime}"`);
    }
    if (stepType === 'media') {
        const slug = readNestedString(step, 'nodeTypeSlug') ??
            readNestedString(step.config, 'nodeTypeSlug') ??
            readNestedString(step.parameters, 'nodeTypeSlug') ??
            readNestedString(step, 'slug') ??
            readNestedString(step.config, 'slug') ??
            readNestedString(step.parameters, 'slug');
        if (!slug) {
            throw new RunnerProfileResolutionError(`Workflow media step "${formatStepId(step)}" is missing nodeTypeSlug`);
        }
        const profile = exports.MEDIA_NODE_PROFILE_BY_SLUG[slug];
        if (!profile) {
            throw new RunnerProfileResolutionError(`Workflow media step "${formatStepId(step)}" has no runner profile mapping for node type slug "${slug}"`);
        }
        return profile;
    }
    if (CORE_STEP_TYPES.has(stepType))
        return 'core';
    throw new RunnerProfileResolutionError(`Workflow step "${formatStepId(step)}" has no runner profile mapping for type "${stepType}"`);
}
function readNestedString(value, key) {
    if (!value || typeof value !== 'object')
        return undefined;
    return asString(value[key]);
}
function asString(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
function formatStepId(step) {
    return asString(step.id) ?? '<unknown>';
}
